import { forwardRef, type ReactNode, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Gray200 } from '@zep-us/zus/tokens';
import { Trans, useTranslation } from 'next-i18next';
import { ZepquizLogoIcon } from '@/icons';
import { cn } from '@/utils';
import { PAGE_UI_CONFIG } from './const';
import { usePaginationContext, useWorksheetAccessContext } from './context';
export const QUIZ_WORKSHEET_PAGE_CLASS_NAME = 'quiz-worksheet-page';
export const QUIZ_WORKSHEET_PAGE_BACKGROUND_LOGO_CLASS_NAME = 'quiz-worksheet-page-background-logo';
const PageInViewMarker = forwardRef<HTMLDivElement>((_, ref) => {
  return <div ref={ref} className="absolute top-[50%] w-full"></div>;
});
PageInViewMarker.displayName = 'PageInViewMarker';
export const QuizWorksheetTwoColumnLayout = ({
  header,
  footer,
  leftColumnItems,
  rightColumnItems,
  isHidden = false,
  pageNumber
}: {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  leftColumnItems: React.ReactNode[];
  rightColumnItems: React.ReactNode[];
  isHidden?: boolean;
  pageNumber?: number;
}) => {
  const {
    handleCurrentPageChange
  } = usePaginationContext();
  const {
    isPro
  } = useWorksheetAccessContext();
  const {
    ref,
    inView
  } = useInView({
    threshold: 0
  });
  useEffect(() => {
    if (inView && pageNumber) {
      handleCurrentPageChange(pageNumber);
    }
  }, [inView, pageNumber, handleCurrentPageChange]);
  return <div style={{
    height: `${PAGE_UI_CONFIG.height}px`,
    paddingBottom: `${PAGE_UI_CONFIG.paddingBottom}px`
  }} className={cn('flex flex-col w-full bg-white p-lg relative', !isHidden && QUIZ_WORKSHEET_PAGE_CLASS_NAME, isHidden && 'invisible absolute')} data-sentry-component="QuizWorksheetTwoColumnLayout" data-sentry-source-file="QuizWorksheetTwoColumnLayout.tsx">
      <PageInViewMarker ref={ref} data-sentry-element="PageInViewMarker" data-sentry-source-file="QuizWorksheetTwoColumnLayout.tsx" />
      {header}
      <div className={cn('relative flex w-full flex-row justify-center gap-sm  h-full overflow-hidden px-xs bg-transparent z-10')}>
        <QuizWorksheetColumn className="left-0 pl-xs pr-sm" data-sentry-element="QuizWorksheetColumn" data-sentry-source-file="QuizWorksheetTwoColumnLayout.tsx">
          {leftColumnItems.map((item, index) => <article key={index}>{item}</article>)}
        </QuizWorksheetColumn>
        <div className="border-x-[0.5px] border-alternative" />
        <QuizWorksheetColumn className="right-0 pl-sm pr-xs" data-sentry-element="QuizWorksheetColumn" data-sentry-source-file="QuizWorksheetTwoColumnLayout.tsx">
          {rightColumnItems.map((item, index) => <article key={index}>{item}</article>)}
        </QuizWorksheetColumn>
      </div>
      {footer}
      <div className="absolute inset-0 flex items-center justify-center">
        <ZepquizLogoIcon className={cn('h-[394px] w-[375px] text-gray-200 opacity-40', QUIZ_WORKSHEET_PAGE_BACKGROUND_LOGO_CLASS_NAME)} fill={Gray200} width={375} height={394} data-sentry-element="ZepquizLogoIcon" data-sentry-source-file="QuizWorksheetTwoColumnLayout.tsx" />
      </div>
      {!isPro && !isHidden && <PreviewBlocker />}
    </div>;
};
const PreviewBlocker = () => {
  const {
    t
  } = useTranslation(['common']);
  return <div className="absolute inset-0 z-20 flex items-center justify-center px-xl" data-sentry-component="PreviewBlocker" data-sentry-source-file="QuizWorksheetTwoColumnLayout.tsx">
      <div className="rounded-[10px] border border-opacity-assistive bg-opacity-violet-2 px-xl py-lg text-center text-heading-sm-bold text-default backdrop-blur-[4px]">
        <p>{t('common:quizWorksheet.previewBlocker.title')}</p>
        <p className="break-all">
          <Trans t={t} i18nKey="common:quizWorksheet.previewBlocker.description" components={{
          Highlight: <span className="text-primary" />,
          Br: <br />
        }} data-sentry-element="Trans" data-sentry-source-file="QuizWorksheetTwoColumnLayout.tsx" />
        </p>
      </div>
    </div>;
};
export const QUIZ_WORKSHEET_COLUMN_CLASS_NAME = 'quiz-worksheet-column';
export const QuizWorksheetColumn = ({
  children,
  className
}: {
  children: ReactNode[];
  className?: string;
}) => {
  return <div className={cn('absolute w-[50%] flex grow shrink-0 flex-col', QUIZ_WORKSHEET_COLUMN_CLASS_NAME, className)} style={{
    paddingTop: `${PAGE_UI_CONFIG.columnPaddingTop}px`,
    rowGap: `${PAGE_UI_CONFIG.questionItemGap}px`
  }} data-sentry-component="QuizWorksheetColumn" data-sentry-source-file="QuizWorksheetTwoColumnLayout.tsx">
      {children}
    </div>;
};